import { useState, useEffect } from 'react'
import { Form as FinalForm, Field } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import arrayMutators from 'final-form-arrays'
import { useTranslation } from 'react-i18next'
import { styled, Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { Grid, Typography, Tooltip, Button } from '@mui/material'
import Dialog from '../../control/Dialog'
import Textbox from '../../control/Textbox'
import NumberTextbox from '../../control/NumberTextbox'
import useMux, { Form, validate } from '../../hooks/backoffice/useMux'
import BackIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import AddIcon from '@mui/icons-material/AddRounded'
import MinusIcon from '@mui/icons-material/RemoveRounded'
import { useSelector } from 'react-redux'
import { RootStateType } from '../../redux/reducer/rootReducer'

type ManageMuxFormProps = {}

const ManageMuxForm = (props: ManageMuxFormProps) => {
    const {
        dataList,
        alertMessageError,
        openDialogError,
        alertMessageOk,
        openDialogBack,
        openDialogSave,
        openDialogOk,
        onClickDeleteFieldArrayItem,
        onClickConfirmSave,
        onClickCancelSave,
        onClickBack,
        onClickForceBack,
        onClickCancelBack,
        onClickConfirmBack,
        onClickOk,
        onSubmitForm,
        callServiceGetById,
    } = useMux()
    const { t } = useTranslation()
    const classes = useStyles()

    const validates = (values: Form) => {
        const validation = validate(values, t) || {}
        return validation
    }

    const [initialValues, setInitialValues] = useState<Form>({})
    const [titleName, setTitleName] = useState<string>()
    const [muxId, setMuxId] = useState<number>(0)

    const { isLoggedIn } = useSelector(({ profile }: RootStateType) => profile)

    useEffect(() => {
        const url = globalThis.location.href
        const splitUrl = url.split('/')
        const id = parseInt(splitUrl[splitUrl.length - 2])
        setTitleName(`${t('BUTTON.EDIT')} MUX ${id}`)
        setMuxId(id)
    }, [dataList])

    useEffect(() => {
        if (isLoggedIn) {
            const url = globalThis.location.href
            const splitUrl = url.split('/')
            const id = parseInt(splitUrl[splitUrl.length - 2])
            setMuxId(id)
            callServiceGetInitial(id)
        }
    }, [isLoggedIn])

    const callServiceGetInitial = async (id: number) => {
        const { result } = await callServiceGetById(id)

        if (result?.data) {
            setInitialValues({
                owner: result?.data.provider,
                channels:
                    result?.data.channels?.length > 0
                        ? result?.data?.channels?.map((item: any) => ({
                              url: item?.urlLogo,
                              no: item?.no,
                              name: item?.name,
                          }))
                        : [
                              {
                                  url: '',
                                  no: '',
                                  name: '',
                              },
                          ],
            })
        }
    }

    return (
        <>
            <FinalForm
                onSubmit={onSubmitForm}
                validate={validates}
                initialValues={initialValues}
                mutators={{
                    ...arrayMutators,
                }}
            >
                {({ handleSubmit, values: formData, dirty, form }) => (
                    <form onSubmit={handleSubmit}>
                        <StyledDivHeader>
                            <Tooltip title={`กลับไปหน้า${t('MENU.MUX_MANAGEMENT')}`} placement="top">
                                <Button
                                    onClick={() => {
                                        onClickBack(dirty)
                                    }}
                                    className={classes.backButton}
                                >
                                    <IconBack />
                                </Button>
                            </Tooltip>
                            <StyledTypoHeader variant="h6">{titleName}</StyledTypoHeader>
                        </StyledDivHeader>
                        <StyledDivForm>
                            <Grid container className={classes.container}>
                                <Grid item xs={12}>
                                    <Field
                                        name="owner"
                                        label={`ชื่อผู้ให้บริการโครงข่ายฯ`}
                                        placeholder={`ชื่อผู้ให้บริการโครงข่ายฯ`}
                                        component={Textbox}
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} style={{ paddingTop: '16px' }}>
                                    <StyledDivHeaderFieldArray>
                                        <StyledTypoTitleFieldArray>{'รายการช่อง'}</StyledTypoTitleFieldArray>
                                        {formData?.channels &&
                                            formData?.channels?.length <
                                                parseInt(`${process.env.REACT_APP_MUX_CHANNEL_MAX}`) &&
                                            formData?.channels[formData?.channels?.length - 1].url &&
                                            formData?.channels[formData?.channels?.length - 1].no &&
                                            formData?.channels[formData?.channels?.length - 1].no >= 0 &&
                                            formData?.channels[formData?.channels?.length - 1].name && (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => {
                                                        form.mutators?.push('channels', { url: '', no: '', name: '' })
                                                    }}
                                                    classes={{ root: classes.addChannelButton }}
                                                >
                                                    <AddIcon />
                                                </Button>
                                            )}
                                    </StyledDivHeaderFieldArray>
                                    <FieldArray name="channels">
                                        {({ fields }) =>
                                            fields.map((name, index) => (
                                                <Grid
                                                    container
                                                    spacing={1}
                                                    key={`${name}_${index}`}
                                                    justifyContent="space-between"
                                                >
                                                    <Grid item xs={5}>
                                                        <Field
                                                            name={`${name}.url`}
                                                            label={`URL รูปช่องรายการ`}
                                                            placeholder={`URL รูปช่องรายการ`}
                                                            component={Textbox}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <Field
                                                            name={`${name}.no`}
                                                            label={`เลขช่องรายการ`}
                                                            placeholder={`เลขช่องรายการ`}
                                                            inputType="number"
                                                            component={NumberTextbox}
                                                            min={0}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid
                                                        item
                                                        xs={
                                                            formData?.channels && formData?.channels?.length > 1 ? 3 : 4
                                                        }
                                                    >
                                                        <Field
                                                            name={`${name}.name`}
                                                            label={`ชื่อช่องรายการ`}
                                                            placeholder={`ชื่อช่องรายการ`}
                                                            component={Textbox}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    {formData?.channels && formData?.channels?.length > 1 ? (
                                                        <Grid item container xs={1} justifyContent="flex-end">
                                                            <Grid item>
                                                                <Button
                                                                    variant="contained"
                                                                    color="error"
                                                                    onClick={() =>
                                                                        onClickDeleteFieldArrayItem(
                                                                            fields.remove,
                                                                            index,
                                                                            fields.value[index]?.name || 0,
                                                                        )
                                                                    }
                                                                    classes={{ root: classes.deleteChannelButton }}
                                                                >
                                                                    <MinusIcon />
                                                                </Button>
                                                            </Grid>
                                                        </Grid>
                                                    ) : null}
                                                </Grid>
                                            ))
                                        }
                                    </FieldArray>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography>
                                        <label style={{ color: '#d32f2f' }}>{'** '}</label>
                                        {
                                            'หมายเหตุ เพื่อความสวยงาม ควรใช้รูปภาพโลโก้ ขนาด 174x120 pixels และไฟล์สกุล png'
                                        }
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} className={classes.divButtonGroup}>
                                    <div className={classes.divButton}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => {
                                                onClickBack?.(dirty)
                                            }}
                                            classes={{ root: classes.button }}
                                        >
                                            {t('BUTTON.CANCEL')}
                                        </Button>
                                    </div>
                                    <div className={classes.divButton}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            classes={{ root: classes.button }}
                                        >
                                            {t('BUTTON.SAVE')}
                                        </Button>
                                    </div>
                                </Grid>
                            </Grid>
                        </StyledDivForm>
                    </form>
                )}
            </FinalForm>
            <Dialog
                type="confirm"
                title={t('ALERT.TITLE.NOTIFY')}
                content={t('ALERT.CONTENT.DISCARD_CHANGE')}
                isOpen={openDialogBack ? true : false}
                onClickCancel={onClickConfirmBack}
                onClickConfirm={onClickCancelBack}
                labelButtonCancel={t('BUTTON.LEAVE')}
                labelButtonConfirm={t('BUTTON.STAY')}
                disableClickBackdrop
                hideCloseButton
            />
            <Dialog
                type="confirm"
                title={t('ALERT.TITLE.NOTIFY')}
                content={'ยืนยันบันทึกข้อมูล'}
                isOpen={openDialogSave}
                onClickConfirm={() => {
                    onClickConfirmSave(muxId)
                }}
                onClickCancel={onClickCancelSave}
                labelButtonCancel={t('BUTTON.CANCEL')}
                labelButtonConfirm={t('BUTTON.CONFIRM')}
                disableClickBackdrop
                hideCloseButton
            />
            <Dialog
                type="ok"
                title={t('ALERT.TITLE.NOTIFY')}
                content={alertMessageOk}
                isOpen={openDialogOk}
                onClickOk={onClickOk}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
            <Dialog
                type="ok"
                severity="error"
                title={t('ALERT.TITLE.ERROR')}
                content={alertMessageError}
                isOpen={openDialogError}
                onClickOk={onClickOk}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
        </>
    )
}

export default ManageMuxForm

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    divButtonGroup: {
        display: 'flex',
        justifyContent: 'end',
        paddingTop: '12px',
        paddingBottom: '15px',
    },
    divButton: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginLeft: theme.spacing(1),
    },
    button: {
        width: '125px',
        minHeight: '37px',
        fontSize: '14px !important',
    },
    checkbox: {
        color: `${theme.palette.primary.dark} !important`,
        padding: '5px 10px !important',
    },
    checkboxChecked: {
        color: `${theme.palette.primary.dark} !important`,
        padding: '5px 10px !important',
    },
    icon: {
        fontSize: '70px !important',
        color: '#318AF14D',
        opacity: 30,
    },
    iconButton: {
        marginTop: '25px !important',
    },
    backButton: {
        padding: '0px !important',
        minWidth: '25px !important',
        minHeight: '25px !important',
        marginRight: '10px !important',
        color: `transparent !important`,
    },
    addChannelButton: {
        minWidth: '25px !important',
        minHeight: '25px !important',
        padding: '0px !important',
    },
    deleteChannelButton: {
        minWidth: '25px !important',
        minHeight: '25px !important',
        padding: '0px !important',
        marginTop: '30px !important',
    },
}))

const StyledDivHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    paddingBottom: '15px',
}))

const StyledTypoHeader = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
}))

const StyledDivForm = styled('div')(({ theme }) => ({
    width: '100%',
    padding: '15px 30px',
    marginBottom: '15px',
    borderRadius: '5px',
    backgroundColor: theme.palette.text.secondary,
    boxShadow: theme.palette.getContrastText('shadow-bottom-gray'),
}))

const StyledTypoTitleFieldArray = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
}))

const StyledDivHeaderFieldArray = styled('div')(({ theme }) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
}))

const IconBack = styled(BackIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}))
